import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const POPULATION_DATA_URL =
  'https://ourworldindata.org/grapher/population.csv?csvType=filtered&useColumnShortNames=true';
const GHG_DATA_URL =
  'https://ourworldindata.org/grapher/total-ghg-emissions.csv?csvType=full&useColumnShortNames=true';
const SAFE_GHG_GT = 2.51;
const SAFE_GHG_MT = SAFE_GHG_GT * 1000;
const CONSTRUCTION_QUOTAS = [2.5, 5, 10, 15];
const RING_COLORS = ['#059669', '#10b981', '#34d399', '#6ee7b7'];
const SUMMARY_RING_COLORS = ['#dc2626', '#fca5a5'];

const parseYear = (row) => Number(row.Year ?? row.year);

const buildSeries = (rows, field, matchFn) => {
  const series = [];
  rows.forEach((row) => {
    if (matchFn && !matchFn(row)) return;
    const year = parseYear(row);
    if (!Number.isFinite(year)) return;
    const value = Number(row[field]);
    if (!Number.isFinite(value)) return;
    series.push({ year, value });
  });
  series.sort((a, b) => a.year - b.year);
  return series;
};

const buildSeriesWithFallback = (rows, field, primaryMatch, fallbackMatch) => {
  const primary = buildSeries(rows, field, primaryMatch);
  if (primary.length || !fallbackMatch) return primary;
  return buildSeries(rows, field, fallbackMatch);
};

const getLatestPoint = (series) => (series.length ? series[series.length - 1] : null);

const safeRate = (prev, next) => (prev === 0 ? 0 : (next - prev) / prev);

const projectToYear = (series, targetYear) => {
  if (!targetYear || !series.length) {
    return { value: null, year: targetYear ?? null, projected: false, details: null };
  }

  const exact = series.find((entry) => entry.year === targetYear);
  if (exact) {
    return { value: exact.value, year: targetYear, projected: false, details: null };
  }

  const last = series[series.length - 1];
  if (targetYear <= last.year) {
    return { value: last.value, year: last.year, projected: false, details: null };
  }

  const gap = targetYear - last.year;
  const details = {
    baseYear: last.year,
    rates: [],
    avgRate: 0,
    method: 'carry-forward',
  };

  if (series.length >= 3) {
    const a = series[series.length - 3];
    const b = series[series.length - 2];
    const c = series[series.length - 1];
    const rate1 = safeRate(a.value, b.value);
    const rate2 = safeRate(b.value, c.value);
    details.rates = [
      { from: a.year, to: b.year, rate: rate1 },
      { from: b.year, to: c.year, rate: rate2 },
    ];
    details.avgRate = (rate1 + rate2) / 2;
    details.baseYear = c.year;
    details.method = 'avg-2yr';
  } else if (series.length >= 2) {
    const b = series[series.length - 2];
    const c = series[series.length - 1];
    const rate = safeRate(b.value, c.value);
    details.rates = [{ from: b.year, to: c.year, rate }];
    details.avgRate = rate;
    details.baseYear = c.year;
    details.method = 'avg-1yr';
  }

  const projectedValue = last.value * Math.pow(1 + details.avgRate, gap);
  return {
    value: projectedValue,
    year: targetYear,
    projected: true,
    details,
  };
};

const formatRate = (rate) => `${(rate * 100).toFixed(2)}%`;

const buildProjectionNote = (label, projection, year) => {
  if (!projection?.projected || !projection.details || !year) return null;
  const { baseYear, rates, avgRate, method } = projection.details;
  const methodLabel = method === 'avg-2yr'
    ? 'avg of last 2 rates'
    : method === 'avg-1yr'
      ? 'last rate'
      : 'carry-forward';
  const rateText = rates.length
    ? rates.map((r) => `${r.from}->${r.to}: ${formatRate(r.rate)}`).join(', ')
    : 'no prior rate data';
  return `${label} projected from ${baseYear} to ${year} using ${methodLabel} (avg rate ${formatRate(avgRate)}; ${rateText}).`;
};

const formatPopulation = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  return value.toLocaleString();
};

const formatWholeNumber = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const normalizeCountryInput = (value) => {
  if (!value) return '';
  const normalized = value.trim().toLowerCase();
  const map = new Map([
    ['usa', 'United States'],
    ['us', 'United States'],
    ['u.s.', 'United States'],
    ['u.s.a.', 'United States'],
    ['united states of america', 'United States'],
    ['uk', 'United Kingdom'],
    ['u.k.', 'United Kingdom'],
    ['uae', 'United Arab Emirates'],
  ]);
  return map.get(normalized) || value.trim();
};

const findCountryEntity = (locationLower, countryEntities, preferredName) => {
  if (!countryEntities?.length) return null;
  const normalizedPreferred = preferredName?.trim().toLowerCase();
  if (normalizedPreferred) {
    const exact = countryEntities.find(
      (entity) => entity.nameLower === normalizedPreferred
    );
    if (exact) return exact;
  }
  return countryEntities.find((entity) => locationLower.includes(entity.nameLower));
};

const getCityCountryFromLocation = (location, countryEntities) => {
  if (!location) return { city: '', country: '' };
  const trimmed = location.trim();
  if (!trimmed || trimmed.toLowerCase() === 'unknown') {
    return { city: '', country: '' };
  }

  const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
  const locationLower = trimmed.toLowerCase();

  if (parts.length >= 2) {
    const city = parts[0];
    const rawCountry = parts.slice(1).join(', ');
    const normalizedCountry = normalizeCountryInput(rawCountry);
    const matchedEntity = findCountryEntity(locationLower, countryEntities, normalizedCountry);
    return { city, country: matchedEntity?.name || normalizedCountry };
  }

  const rawCountry = normalizeCountryInput(parts[0] || '');
  const matchedEntity = findCountryEntity(locationLower, countryEntities, rawCountry);
  return { city: '', country: matchedEntity?.name || rawCountry };
};

const ConstructionCapacity = ({ location, countryEntities }) => {
  const [populationSeries, setPopulationSeries] = useState([]);
  const [worldPopulationSeries, setWorldPopulationSeries] = useState([]);
  const [ghgSeries, setGhgSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [projectToCurrentYear, setProjectToCurrentYear] = useState(false);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [showSourceTooltip, setShowSourceTooltip] = useState(false);
  const sourceTooltipRef = useRef(null);

  const { country } = useMemo(
    () => getCityCountryFromLocation(location, countryEntities),
    [location, countryEntities]
  );
  const normalizedCountry = useMemo(() => (country || '').trim(), [country]);
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const normalizedCode = useMemo(() => {
    if (!normalizedCountry) return '';
    const match = (countryEntities || []).find(
      (entity) => entity.name?.toLowerCase() === normalizedCountry.toLowerCase()
    );
    return match?.code || '';
  }, [countryEntities, normalizedCountry]);

  useEffect(() => {
    let cancelled = false;

    if (!normalizedCountry) {
      setPopulationSeries([]);
      setWorldPopulationSeries([]);
      setGhgSeries([]);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    const load = async () => {
      setIsLoading(true);
      setLoadingProgress(0);
      setError(null);

      try {
        const countryParam = normalizedCode || normalizedCountry;
        const combinedParam = `${countryParam}~OWID_WRL`;

        const [popDataResponse, ghgDataResponse] = await Promise.all([
          fetch(`${POPULATION_DATA_URL}&country=${encodeURIComponent(combinedParam)}`)
            .then((response) => {
              setLoadingProgress((prev) => Math.max(prev, 45));
              return response;
            }),
          fetch(`${GHG_DATA_URL}&country=${encodeURIComponent(countryParam)}`)
            .then((response) => {
              setLoadingProgress((prev) => Math.max(prev, 70));
              return response;
            }),
        ]);

        if (!popDataResponse.ok) {
          throw new Error(`Population data request failed (${popDataResponse.status})`);
        }
        if (!ghgDataResponse.ok) {
          throw new Error(`GHG data request failed (${ghgDataResponse.status})`);
        }

        const [popCsvText, ghgCsvText] = await Promise.all([
          popDataResponse.text().then((text) => {
            setLoadingProgress((prev) => Math.max(prev, 85));
            return text;
          }),
          ghgDataResponse.text().then((text) => {
            setLoadingProgress((prev) => Math.max(prev, 90));
            return text;
          }),
        ]);

        const populationRows = d3.csvParse(popCsvText);
        const ghgRows = d3.csvParse(ghgCsvText);

        const matchesCountry = (row) => {
          const code = (row.Code || row.code || '').trim();
          const entity = (row.Entity || row.entity || '').trim();
          if (normalizedCode && code) {
            return code.toLowerCase() === normalizedCode.toLowerCase();
          }
          if (normalizedCountry && entity) {
            return entity.toLowerCase() === normalizedCountry.toLowerCase();
          }
          return false;
        };

        const matchesWorld = (row) => {
          const code = (row.Code || row.code || '').trim();
          const entity = (row.Entity || row.entity || '').trim();
          return entity === 'World' || code === 'OWID_WRL';
        };

        const populationSeriesNext = buildSeriesWithFallback(
          populationRows,
          'population_historical',
          matchesCountry,
          (row) => {
            const entity = (row.Entity || row.entity || '').trim().toLowerCase();
            return normalizedCountry && entity.includes(normalizedCountry.toLowerCase());
          }
        );
        const worldPopulationSeriesNext = buildSeriesWithFallback(
          populationRows,
          'population_historical',
          matchesWorld
        );
        const ghgSeriesNext = buildSeriesWithFallback(
          ghgRows,
          'annual_emissions_ghg_total_co2eq',
          matchesCountry,
          (row) => {
            const entity = (row.Entity || row.entity || '').trim().toLowerCase();
            return normalizedCountry && entity.includes(normalizedCountry.toLowerCase());
          }
        );
        if (!cancelled) {
          setPopulationSeries(populationSeriesNext);
          setWorldPopulationSeries(worldPopulationSeriesNext);
          setGhgSeries(ghgSeriesNext);
          setLoadingProgress(100);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch construction capacity data', err);
          setError('Server failed for this information. Try again later.');
          setPopulationSeries([]);
          setWorldPopulationSeries([]);
          setGhgSeries([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [normalizedCountry, normalizedCode]);

  const populationLatest = useMemo(() => getLatestPoint(populationSeries), [populationSeries]);
  const ghgLatest = useMemo(() => getLatestPoint(ghgSeries), [ghgSeries]);

  const targetYear = useMemo(() => {
    if (projectToCurrentYear) {
      return currentYear;
    }
    const years = [populationLatest?.year, ghgLatest?.year].filter((year) => Number.isFinite(year));
    if (!years.length) return null;
    return Math.max(...years);
  }, [populationLatest, ghgLatest, projectToCurrentYear, currentYear]);

  const alignedPopulation = useMemo(
    () => projectToYear(populationSeries, targetYear),
    [populationSeries, targetYear]
  );
  const alignedWorldPopulation = useMemo(
    () => projectToYear(worldPopulationSeries, targetYear),
    [worldPopulationSeries, targetYear]
  );
  const alignedGhg = useMemo(() => projectToYear(ghgSeries, targetYear), [ghgSeries, targetYear]);

  const shareValue = useMemo(() => {
    const popValue = alignedPopulation.value;
    const worldValue = alignedWorldPopulation.value;
    if (!Number.isFinite(popValue) || !Number.isFinite(worldValue) || worldValue <= 0) {
      return null;
    }
    return (popValue / worldValue) * 100;
  }, [alignedPopulation.value, alignedWorldPopulation.value]);

  const ghgMt = useMemo(() => {
    if (!Number.isFinite(alignedGhg.value)) return null;
    return alignedGhg.value / 1_000_000;
  }, [alignedGhg.value]);

  const safeShareMt = useMemo(() => {
    if (!Number.isFinite(shareValue)) return null;
    return (SAFE_GHG_MT * shareValue) / 100;
  }, [shareValue]);

  const reductionPercent = useMemo(() => {
    if (!Number.isFinite(ghgMt) || !Number.isFinite(safeShareMt) || ghgMt <= 0) return null;
    const diff = ghgMt - safeShareMt;
    if (diff <= 0) return 0;
    return (diff / ghgMt) * 100;
  }, [ghgMt, safeShareMt]);

  const newBuildingsShareMt = useMemo(() => {
    if (!Number.isFinite(safeShareMt)) return null;
    return safeShareMt * 0.1;
  }, [safeShareMt]);

  const allowableAreas = useMemo(() => {
    if (!Number.isFinite(newBuildingsShareMt)) return [];
    const budgetKg = newBuildingsShareMt * 1_000_000;
    return CONSTRUCTION_QUOTAS.map((quota) => ({
      quota,
      floorArea: budgetKg / quota,
    }));
  }, [newBuildingsShareMt]);

  const maxAllowableArea = useMemo(
    () => allowableAreas.reduce((max, item) => Math.max(max, item.floorArea || 0), 0),
    [allowableAreas]
  );

  const ringChart = useMemo(() => {
    if (!allowableAreas.length) return null;
    const ringGap = 6;
    const summaryGap = 10;
    const ringThickness = 16;
    const hasSummaryRing = Number.isFinite(safeShareMt) && Number.isFinite(ghgMt);
    const countryLabel = normalizedCountry || 'this country';
    const ringCount = allowableAreas.length + (hasSummaryRing ? 1 : 0);
    const outerRadius =
      ringCount * ringThickness +
      (ringCount - 1) * ringGap +
      (hasSummaryRing ? summaryGap : 0) +
      16;
    const size = Math.max(outerRadius * 2, 240);
    const offset = hasSummaryRing ? 1 : 0;
    const arcs = allowableAreas.map((entry, index) => {
      const ringOuter =
        outerRadius -
        (index + offset) * (ringThickness + ringGap) -
        (hasSummaryRing ? summaryGap : 0);
      const ringInner = ringOuter - ringThickness;
      const ratio = maxAllowableArea ? Math.min(entry.floorArea / maxAllowableArea, 1) : 0;
      return {
        ...entry,
        ringInner,
        ringOuter,
        ratio,
        color: RING_COLORS[index % RING_COLORS.length],
      };
    });
    let summaryRing = null;
    if (hasSummaryRing) {
      const total = safeShareMt + ghgMt;
      const safeRatio = total > 0 ? safeShareMt / total : 0;
      const ringOuter = outerRadius;
      const ringInner = ringOuter - ringThickness;
      summaryRing = {
        ringInner,
        ringOuter,
        segments: [
          {
            key: 'summary-safe',
            label: `Safe operating space for ${countryLabel}`,
            value: safeShareMt,
            startAngle: -Math.PI / 2,
            endAngle: -Math.PI / 2 + safeRatio * Math.PI * 2,
            color: SUMMARY_RING_COLORS[0],
          },
          {
            key: 'summary-current',
            label: 'Current GHG emissions',
            value: ghgMt,
            startAngle: -Math.PI / 2 + safeRatio * Math.PI * 2,
            endAngle: -Math.PI / 2 + Math.PI * 2,
            color: SUMMARY_RING_COLORS[1],
          },
        ],
      };
    }
    return { size, arcs, summaryRing };
  }, [allowableAreas, maxAllowableArea, safeShareMt, ghgMt, normalizedCountry]);

  const hoveredSegmentData = useMemo(() => {
    if (!hoveredSegment) return null;
    if (hoveredSegment.kind === 'quota') {
      const entry = allowableAreas.find((item) => item.quota === hoveredSegment.quota);
      if (!entry) return null;
      const ratio = maxAllowableArea ? Math.min(entry.floorArea / maxAllowableArea, 1) : 0;
      return {
        kind: 'quota',
        quota: entry.quota,
        value: entry.floorArea,
        ratio,
      };
    }
    if (hoveredSegment.kind === 'summary') {
      return {
        kind: 'summary',
        label: hoveredSegment.label,
        value: hoveredSegment.value,
      };
    }
    return null;
  }, [hoveredSegment, allowableAreas, maxAllowableArea]);

  const projectionNotes = useMemo(() => {
    const notes = [];
    const popNote = buildProjectionNote('Population', alignedPopulation, targetYear);
    const worldNote = buildProjectionNote('World population', alignedWorldPopulation, targetYear);
    const ghgNote = buildProjectionNote('GHG emissions', alignedGhg, targetYear);
    if (popNote) notes.push(popNote);
    if (worldNote) notes.push(worldNote);
    if (ghgNote) notes.push(ghgNote);
    if (projectToCurrentYear && targetYear === currentYear && notes.length === 0) {
      notes.push(`All values already reported for ${currentYear}; no projection needed.`);
    }
    return notes;
  }, [alignedPopulation, alignedWorldPopulation, alignedGhg, targetYear, projectToCurrentYear, currentYear]);

  const showProjectionInfo = projectionNotes.length > 0;
  const tooltipDetails = useMemo(() => {
    if (!showProjectionInfo) return null;
    return {
      notes: projectionNotes,
      latest: {
        population: populationLatest,
        worldPopulation: getLatestPoint(worldPopulationSeries),
        ghg: ghgLatest,
      },
    };
  }, [showProjectionInfo, projectionNotes, populationLatest, worldPopulationSeries, ghgLatest]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!sourceTooltipRef.current) return;
      if (!sourceTooltipRef.current.contains(event.target)) {
        setShowSourceTooltip(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <Card className="border border-emerald-100 bg-white/80 shadow-md">
      <CardHeader className="space-y-2 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="w-full">
            <CardTitle className="text-xl font-semibold text-gray-900 uppercase text-center mb-6">
              Construction capacity - {normalizedCountry || 'Unknown'}
            </CardTitle>
            <CardDescription className="text-black text-justify">
            Summarises how a country’s population share and total GHG emissions compare against a global safe 
            operating space benchmark aligned with 'IPCC AR6 carbon budget guidance', and frames the construction 
            quota available for new buildings in the country using UNEP buildings sector context.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 text-xs text-black">
            <span>Project for {currentYear} case</span>
            <button
              type="button"
              onClick={() => setProjectToCurrentYear((prev) => !prev)}
              aria-pressed={projectToCurrentYear}
              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                projectToCurrentYear
                  ? 'border-emerald-600 bg-emerald-600 text-white'
                  : 'border-[#cbd5cb] bg-white text-black'
              }`}
            >
              {projectToCurrentYear ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-sm text-black pb-3 pt-1">
        {isLoading && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-black">
              <span className="inline-flex h-4 w-4 rounded-full border-2 border-black/30 border-t-black animate-spin" />
              Loading population and emissions data...
            </div>
            <div className="h-1 w-full overflow-hidden rounded-full bg-black/10">
              <div
                className="h-full rounded-full bg-black transition-all duration-300"
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
          </div>
        )}
        {!isLoading && error && <p className="text-black">{error}</p>}
        {!isLoading && !error && (
          <div className="space-y-3">
            <div className="grid gap-6 md:grid-cols-[1.35fr_1fr] md:gap-0">
              <div className="space-y-4">
                {reductionPercent != null && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[15px] font-bold text-red-600 text-center">
                    {reductionPercent.toFixed(1)}% reduction needed to stay within the safe operating space
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-black">
                  <span>Key metrics</span>
                  {showProjectionInfo && tooltipDetails ? (
                    <span className="relative inline-flex items-center">
                      <span className="group inline-flex items-center rounded-full border border-[#cbd5cb] px-1 text-[10px] text-black cursor-help">
                        info
                        <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-[360px] max-w-[92vw] -translate-x-1/2 rounded-md border border-[#d5dacc] bg-white px-3 py-2 text-[11px] text-black shadow-lg opacity-0 transition-opacity group-hover:opacity-100 md:w-[440px]">
                          <div className="space-y-2">
                            <div className="font-semibold text-black">Projection details</div>
                            <div className="space-y-1">
                              {tooltipDetails.notes.map((note, index) => (
                                <div key={`proj-note-${index}`} className="leading-snug">
                                  {note}
                                </div>
                              ))}
                            </div>
                            <div className="border-t border-[#e2e8d8] pt-2">
                              <div className="font-semibold text-black">Latest reported data</div>
                              <div className="space-y-1">
                                <div>
                                  Population: {tooltipDetails.latest.population?.value?.toLocaleString() || 'N/A'}
                                  {tooltipDetails.latest.population?.year ? ` (${tooltipDetails.latest.population.year})` : ''}
                                </div>
                                <div>
                                  World population: {tooltipDetails.latest.worldPopulation?.value?.toLocaleString() || 'N/A'}
                                  {tooltipDetails.latest.worldPopulation?.year ? ` (${tooltipDetails.latest.worldPopulation.year})` : ''}
                                </div>
                                <div>
                                  GHG emissions: {tooltipDetails.latest.ghg?.value?.toLocaleString() || 'N/A'}
                                  {tooltipDetails.latest.ghg?.year ? ` (${tooltipDetails.latest.ghg.year})` : ''}
                                </div>
                              </div>
                            </div>
                            <div className="border-t border-[#e2e8d8] pt-2">
                              <div className="font-semibold text-black">Additional metrics</div>
                              <div className="space-y-1">
                                <div>
                                  World population: {formatWholeNumber(alignedWorldPopulation.value)}
                                  {targetYear ? ` (${targetYear})` : ''}
                                </div>
                                <div>
                                  Safe operating space for world GHG emissions (AR6+ high confidence):{' '}
                                  {SAFE_GHG_MT.toFixed(0)} MtCO2e/yr
                                </div>
                              </div>
                            </div>
                          </div>
                        </span>
                      </span>
                    </span>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
                  <div className="rounded-lg border border-emerald-300 bg-white px-3 py-2 shadow-sm">
                    <div className="text-sm text-black/70">{normalizedCountry || 'Country'} population</div>
                    <div className="text-sm font-semibold text-black">
                      {formatWholeNumber(alignedPopulation.value)}
                      {targetYear ? ` (${targetYear})` : ''}
                    </div>
                  </div>
                  <div className="rounded-lg border border-emerald-300 bg-white px-3 py-2 shadow-sm">
                    <div className="text-sm text-black/70">Share of world population</div>
                    <div className="text-sm font-semibold text-black">
                      {shareValue != null ? `${shareValue.toFixed(2)}%` : 'N/A'}
                    </div>
                  </div>
                  <div className="rounded-lg border border-emerald-300 bg-white px-3 py-2 shadow-sm">
                    <div className="text-sm text-black/70">
                      Current GHG emissions of {normalizedCountry || 'country'}
                    </div>
                    <div className="text-sm font-semibold text-black">
                      {ghgMt != null ? (
                        <>
                          {ghgMt.toFixed(2)} MtCO<sub>2</sub>e/yr
                        </>
                      ) : (
                        'N/A'
                      )}
                      {targetYear ? ` (${targetYear})` : ''}
                    </div>
                  </div>
                  <div className="rounded-lg border border-emerald-300 bg-white px-3 py-2 shadow-sm">
                    <div className="text-sm text-black/70">
                      Safe operating space for {normalizedCountry || 'country'}
                    </div>
                    <div className="text-sm font-semibold text-black">
                      {safeShareMt != null ? (
                        <>
                          {safeShareMt.toFixed(2)} MtCO<sub>2</sub>e/yr
                        </>
                      ) : (
                        'N/A'
                      )}
                    </div>
                  </div>
                </div>
                <div className="pt-2 space-y-2">
                  <div className="text-sm font-semibold text-black">
                    Construction quota - Allowable floor area for {normalizedCountry} (m<sup>2</sup>)
                  </div>
                  <div className="text-sm text-black">
                    Safe operating space for GHG for new buildings (10% - UNEP, 2024):
                    <span className="font-semibold text-black">
                      {newBuildingsShareMt != null ? (
                        <>
                          {' '}{newBuildingsShareMt.toFixed(2)} MtCO<sub>2</sub>e/yr
                        </>
                      ) : (
                        ' N/A'
                      )}
                    </span>
                  </div>
                  {allowableAreas.length > 0 ? (
                    <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-4 relative top-3">
                      {allowableAreas.map((entry) => {
                        const borderColor =
                          entry.quota === 2.5
                            ? '#16a34a'
                            : entry.quota === 5
                              ? '#ffbf00'
                              : entry.quota === 10
                                ? '#ea580c'
                                : '#dc2626';
                        return (
                          <div
                            key={`quota-value-${entry.quota}`}
                            className="rounded-lg border-[3px] bg-white px-3 py-2 shadow-sm"
                            style={{ borderColor }}
                          >
                          <div className="text-sm text-black/70 text-center">
                            {entry.quota} kgCO<sub>2</sub>e/m<sup>2</sup>/yr
                          </div>
                          <div className="mt-2 text-sm font-semibold text-black break-words text-center">
                            {formatWholeNumber(entry.floorArea)} m<sup>2</sup>
                          </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-black/70">
                      Allowable floor area data unavailable.
                    </div>
                  )}
                </div>
              </div>
              <div className="md:pl-6">
                <div className="-mt-2 flex items-start justify-between gap-2">
                  <div className="text-sm font-semibold text-black">Allowable floor area chart</div>
                  <div className="text-[10px] text-black/50">Relative to max</div>
                </div>
                {allowableAreas.length > 0 && ringChart ? (
                  <div className="mt-0 pt-8 flex w-full flex-col items-center gap-3">
                    <div
                      className="relative"
                      onMouseLeave={() => setHoveredSegment(null)}
                    >
                      {hoveredSegmentData ? (
                        <div className="pointer-events-none absolute -top-2 left-1/2 z-10 w-72 -translate-x-1/2 -translate-y-full rounded-md border border-emerald-100 bg-white px-3 py-2 text-[11px] text-black shadow-md">
                          <div className="font-semibold text-black">
                            {hoveredSegmentData.kind === 'quota' ? (
                              <>
                                {hoveredSegmentData.quota} kgCO<sub>2</sub>e/m<sup>2</sup>/yr
                              </>
                            ) : (
                              hoveredSegmentData.label
                            )}
                          </div>
                          {hoveredSegmentData.kind === 'quota' ? (
                            <div>
                              Allowable floor area: {formatWholeNumber(hoveredSegmentData.value)} m<sup>2</sup>
                            </div>
                          ) : (
                            <div>
                              {Number.isFinite(hoveredSegmentData.value) ? (
                                <>
                                  {hoveredSegmentData.value.toFixed(2)} MtCO<sub>2</sub>e/yr
                                </>
                              ) : (
                                'N/A'
                              )}
                            </div>
                          )}
                        </div>
                      ) : null}
                      <svg
                        className="block h-auto w-full max-w-full"
                        viewBox={`0 0 ${ringChart.size} ${ringChart.size}`}
                        style={{ maxWidth: ringChart.size }}
                      >
                        <g transform={`translate(${ringChart.size / 2}, ${ringChart.size / 2})`}>
                          {ringChart.summaryRing ? (
                            <g>
                              {ringChart.summaryRing.segments.map((segment) => {
                                const segmentArc = d3
                                  .arc()
                                  .innerRadius(ringChart.summaryRing.ringInner)
                                  .outerRadius(ringChart.summaryRing.ringOuter)
                                  .startAngle(segment.startAngle)
                                  .endAngle(segment.endAngle);
                                return (
                                  <path
                                    key={segment.key}
                                    d={segmentArc()}
                                    fill={segment.color}
                                    className="focus:outline-none"
                                    onMouseEnter={() =>
                                      setHoveredSegment({
                                        kind: 'summary',
                                        label: segment.label,
                                        value: segment.value,
                                      })
                                    }
                                    onFocus={() =>
                                      setHoveredSegment({
                                        kind: 'summary',
                                        label: segment.label,
                                        value: segment.value,
                                      })
                                    }
                                    onBlur={() => setHoveredSegment(null)}
                                    role="button"
                                    tabIndex={0}
                                    style={{ cursor: 'pointer' }}
                                  />
                                );
                              })}
                            </g>
                          ) : null}
                          {ringChart.arcs.map((arc, index) => {
                            const isActive = hoveredSegment?.kind === 'quota' && hoveredSegment.quota === arc.quota;
                            const backgroundArc = d3
                              .arc()
                              .innerRadius(arc.ringInner)
                              .outerRadius(arc.ringOuter)
                              .startAngle(0)
                              .endAngle(Math.PI * 2);
                            const valueArc = d3
                              .arc()
                              .innerRadius(arc.ringInner)
                              .outerRadius(arc.ringOuter)
                              .startAngle(-Math.PI / 2)
                              .endAngle(-Math.PI / 2 + arc.ratio * Math.PI * 2);
                            return (
                              <g key={`ring-${arc.quota}`}>
                                <path d={backgroundArc()} fill="#e5e7eb" />
                                <path
                                  d={valueArc()}
                                  fill={arc.color}
                                  stroke={isActive ? '#065f46' : 'none'}
                                  strokeWidth={isActive ? 2 : 0}
                                  className="focus:outline-none"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`${arc.quota} kgCO2e per m2 per year`}
                                  onMouseEnter={() => setHoveredSegment({ kind: 'quota', quota: arc.quota })}
                                  onFocus={() => setHoveredSegment({ kind: 'quota', quota: arc.quota })}
                                  onBlur={() => setHoveredSegment(null)}
                                  style={{ cursor: 'pointer' }}
                                />
                                {isActive && (
                                  <circle
                                    r={(arc.ringInner + arc.ringOuter) / 2}
                                    fill="none"
                                    stroke="#065f46"
                                    strokeDasharray="4 4"
                                    opacity={0.6}
                                  />
                                )}
                                <circle
                                  r={(arc.ringInner + arc.ringOuter) / 2}
                                  fill="none"
                                  stroke={RING_COLORS[index % RING_COLORS.length]}
                                  opacity={0.15}
                                />
                              </g>
                            );
                          })}
                        </g>
                      </svg>
                    </div>
                    <div
                      className="mt-4 mx-auto w-full max-w-full grid grid-cols-2 md:grid-cols-3 justify-items-start gap-x-10 md:gap-x-16 lg:gap-x-20 gap-y-3 px-2 text-[11px] leading-4 text-black text-left transform translate-x-2 md:-translate-x-[30px]"
                      style={{ maxWidth: Math.max(ringChart.size, 360) }}
                    >
                      {ringChart.summaryRing ? (
                        <>
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: SUMMARY_RING_COLORS[0] }}
                            />
                            <span className="whitespace-nowrap">Safe emission</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: SUMMARY_RING_COLORS[1] }}
                            />
                            <span className="whitespace-nowrap">Current emission</span>
                          </div>
                        </>
                      ) : null}
                      {allowableAreas.map((entry, index) => (
                        <div key={`quota-legend-${entry.quota}`} className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: RING_COLORS[index % RING_COLORS.length] }}
                          />
                          <span className="whitespace-nowrap">
                            {entry.quota} kgCO<sub>2</sub>e/m<sup>2</sup>/yr
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 text-sm text-black/70">
                    Allowable floor area data unavailable.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="mt-2 flex items-end justify-end text-xs text-black">
          <span className="relative inline-flex" ref={sourceTooltipRef}>
            <button
              type="button"
              className="cursor-pointer underline decoration-dotted text-black"
              onClick={() => setShowSourceTooltip((prev) => !prev)}
              aria-expanded={showSourceTooltip}
              aria-haspopup="dialog"
            >
              Source
            </button>
            {showSourceTooltip ? (
              <div className="absolute right-0 top-full z-20 mt-2 w-[320px] max-w-[85vw] rounded-md border border-[#d5dacc] bg-white px-3 py-2 text-[11px] text-black shadow-lg">
                <div className="space-y-2">
                  <div>
                    Population data from Our World in Data (HYDE, Gapminder, UN WPP). GHG emissions from Jones et al. (2025).
                  </div>
                  <div>
                    Inspired from{' '}
                    <a
                      className="text-emerald-700 underline"
                      href="https://www.appsheet.com/start/09a210d6-f94c-4827-bcd4-cf77f969192a?platform=desktop#appName=CONSTRUCTIONCAPACITYcalculator-973420253-24-03-27-3&vss=H4sIAAAAAAAAA6XOsQ7CIBQF0H-5M1_AahyMUQeNizhgeSRECk2h1Ybw74LaODeOXN657yWMhh7HKJs7-CX9XluawJEETlNHAlxg5V3svRVgAnvZfsLDSH01Ahn5ymYfKYCnZZz_t53BKHLRaEN97aqydHxd-a6qBLNBZmiHKG-W3gcXk3PJtG-GQOpcR5afEDZu_eykUzuvSqWWNlB-Aa_9LKVlAQAA&view=Overview"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Construction Capacity
                    </a>{' '}
                    by Tomislav Martinović.
                  </div>
                </div>
              </div>
            ) : null}
          </span>
          <span
            className="hidden"
            title="Population data from Our World in Data (HYDE, Gapminder, UN WPP). GHG emissions from Jones et al. (2025). Inspired by Construction Capacity by Tomislav Martinović."
          >
            Source
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConstructionCapacity;
